import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { safeRemoveChannel } from "@/lib/supabase-channel";

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

export function useRealtimeConnection() {
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    // Clean up previous channel if exists
    if (channelRef.current) {
      safeRemoveChannel(channelRef.current);
      channelRef.current = null;
    }

    channelRef.current = supabase
      .channel("connection-status")
      .subscribe((subscriptionStatus) => {
        if (subscriptionStatus === "SUBSCRIBED") {
          setStatus("connected");
        } else if (subscriptionStatus === "CLOSED" || subscriptionStatus === "CHANNEL_ERROR") {
          setStatus("disconnected");
        } else if (subscriptionStatus === "TIMED_OUT") {
          setStatus("disconnected");
        }
      });

    // Handle online/offline events
    const handleOnline = () => {
      setStatus("connecting");
      // Attempt to reconnect the channel
      if (channelRef.current && channelRef.current.state !== "joined") {
        channelRef.current.subscribe();
      }
    };

    const handleOffline = () => {
      setStatus("disconnected");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check initial online status
    if (!navigator.onLine) {
      setStatus("disconnected");
    }

    return () => {
      safeRemoveChannel(channelRef.current);
      channelRef.current = null;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { status, isConnected: status === "connected" };
}
